import type { CSSObject } from '@emotion/styled';
import { MICRO_LOGO } from '@ice-web-app/assets';
import { useGlobalStore } from '@ice-web-app/shared-store';
import { Box, Fade, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useScrollTrigger from '@mui/material/useScrollTrigger';
import classnames from 'classnames';
import { useSettings } from '../../../@core/hooks/useSettings';
import type { ChildrenType } from '../../../@core/types';
import useVerticalNav from '../../../@menu/hooks/useVerticalNav';
import themeConfig from '../../../configs/themeConfig';
import StyledHeader from '../../styles/vertical/StyledHeader';
import { verticalLayoutClasses } from '../../utils/layoutClasses';

type Props = ChildrenType & {
  overrideStyles?: CSSObject;
};

const Navbar = (props: Props) => {
  const { children, overrideStyles } = props;
  const theme = useTheme();
  const isMedium = useMediaQuery('(min-width:1380px)');
  const { showMainSidebar } = useGlobalStore();
  const { isBreakpointReached } = useVerticalNav();
  const { settings } = useSettings();
  const { navbarContentWidth } = settings;

  const trigger = useScrollTrigger({
    threshold: 0,
    disableHysteresis: true,
  });

  const headerFixed = themeConfig.navbar.type === 'fixed';
  const headerStatic = themeConfig.navbar.type === 'static';
  const headerFloating = themeConfig.navbar.floating === true;
  const headerDetached = themeConfig.navbar.detached === true;
  const headerAttached = themeConfig.navbar.detached === false;
  const headerBlur = themeConfig.navbar.blur === true;
  const headerContentCompact = navbarContentWidth === 'compact';
  const headerContentWide = navbarContentWidth === 'wide';

  return (
    <StyledHeader
      theme={theme}
      overrideStyles={overrideStyles}
      className={classnames(verticalLayoutClasses.header, {
        [verticalLayoutClasses.headerFixed]: headerFixed,
        [verticalLayoutClasses.headerStatic]: headerStatic,
        [verticalLayoutClasses.headerFloating]: headerFloating,
        [verticalLayoutClasses.headerDetached]: !headerFloating && headerDetached,
        [verticalLayoutClasses.headerAttached]: !headerFloating && headerAttached,
        [verticalLayoutClasses.headerBlur]: headerBlur,
        [verticalLayoutClasses.headerContentCompact]: headerContentCompact,
        [verticalLayoutClasses.headerContentWide]: headerContentWide,
        scrolled: trigger,
      })}
    >
      {!showMainSidebar && !isBreakpointReached && (
        <Fade in={true}>
          <Box width={260} sx={{ paddingInlineStart: 4 }}>
            <img src={MICRO_LOGO as unknown as string} alt="microSystem logo" />
          </Box>
        </Fade>
      )}

      <div
        className={classnames(verticalLayoutClasses.navbar, 'flex bs-full')}
        {...(isMedium && { style: { paddingLeft: 0 } })}
      >
        {children}
      </div>
    </StyledHeader>
  );
};

export default Navbar;
